import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Users,
  Target,
  Heart,
  Briefcase,
  Award,
  TrendingUp,
  BookOpen,
  CheckCircle2,
  Building2,
  GraduationCap,
  Headphones,
  Rocket,
  Globe,
  Shield,
  Star,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const SoftSkillsTraining = () => {
  const navigate = useNavigate();

  const pageData = {
    h1: 'Soft Skills Training in Chennai',
    metaTitle: 'Soft Skills Training in Chennai | Professional & Corporate Programs',
    metaDescription: 'Enhance communication, leadership, and workplace effectiveness with expert soft skills training in Chennai. Customized programs for students & professionals.',
    metaKeywords: 'soft skills training chennai, communication skills training, leadership development, emotional intelligence, professional etiquette, corporate training chennai',
    canonicalUrl: 'https://isde.academy/training/soft-skills-training-chennai'
  };

  const softSkillCategories = [
    {
      id: 'communication',
      title: 'Communication Skills Training',
      icon: MessageSquare,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      gradient: 'from-blue-500 to-cyan-600',
      description: 'Communication is the foundation of professional success. Soft skills training in Chennai emphasizes verbal and non-verbal communication, professional email and business writing, presentation and public speaking skills, and active listening techniques.',
      topics: [
        'Verbal and non-verbal communication',
        'Professional email and business writing',
        'Presentation and public speaking skills',
        'Active listening and feedback techniques'
      ],
      benefit: 'Strong communication skills improve clarity, confidence, and workplace relationships.'
    },
    {
      id: 'interpersonal',
      title: 'Interpersonal and Teamwork Skills',
      icon: Users,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      gradient: 'from-purple-500 to-pink-600',
      description: 'Modern workplaces require collaboration across departments and cultures. Training focuses on team dynamics and collaboration, conflict resolution techniques, building trust and rapport, and cross-functional teamwork.',
      topics: [
        'Team dynamics and collaboration',
        'Conflict resolution techniques',
        'Building trust and rapport',
        'Cross-functional teamwork'
      ],
      benefit: 'Effective interpersonal skills enhance team performance and workplace harmony.'
    },
    {
      id: 'leadership',
      title: 'Leadership and Management Skills',
      icon: Target,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      gradient: 'from-amber-500 to-orange-600',
      description: 'Leadership development is a key component of advanced soft skills training in Chennai. Programs cover leadership styles and emotional intelligence, decision-making and problem-solving, delegation and performance management, and motivating and influencing teams.',
      topics: [
        'Leadership styles and emotional intelligence',
        'Decision-making and problem-solving',
        'Delegation and performance management',
        'Motivating and influencing teams'
      ],
      benefit: 'These skills are essential for supervisors, managers, and future leaders.'
    },
    {
      id: 'emotional-intelligence',
      title: 'Emotional Intelligence (EQ)',
      icon: Heart,
      color: 'text-rose-600',
      bgColor: 'bg-rose-50',
      gradient: 'from-rose-500 to-red-600',
      description: 'Emotional intelligence plays a vital role in managing stress, relationships, and workplace challenges. Training includes self-awareness and self-regulation, empathy and social awareness, stress and time management, and handling workplace pressure.',
      topics: [
        'Self-awareness and self-regulation',
        'Empathy and social awareness',
        'Stress and time management',
        'Handling workplace pressure'
      ],
      benefit: 'EQ enhances resilience, adaptability, and professional relationships.'
    },
    {
      id: 'professional-etiquette',
      title: 'Professional Etiquette and Workplace Behavior',
      icon: Briefcase,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      gradient: 'from-emerald-500 to-teal-600',
      description: 'Soft skills training in Chennai also focuses on professional conduct, including corporate etiquette and grooming, workplace ethics and integrity, cultural sensitivity, and business meeting and networking etiquette.',
      topics: [
        'Corporate etiquette and grooming',
        'Workplace ethics and integrity',
        'Cultural sensitivity',
        'Business meeting and networking etiquette'
      ],
      benefit: 'These skills enhance professionalism and organizational image.'
    }
  ];

  const benefits = [
    {
      title: 'Improved Career Opportunities',
      description: 'Employers value professionals who demonstrate strong communication, teamwork, and leadership abilities.',
      icon: TrendingUp,
      color: 'emerald'
    },
    {
      title: 'Enhanced Workplace Confidence',
      description: 'Participants gain confidence in meetings, presentations, interviews, and daily interactions.',
      icon: Star,
      color: 'blue'
    },
    {
      title: 'Better Leadership and Team Performance',
      description: 'Organizations benefit from improved collaboration, accountability, and employee engagement.',
      icon: Users,
      color: 'purple'
    },
    {
      title: 'Increased Productivity',
      description: 'Clear communication and effective interpersonal skills reduce errors, delays, and conflicts.',
      icon: Target,
      color: 'orange'
    },
    {
      title: 'Stronger Customer Relationships',
      description: 'Soft skills improve client communication, negotiation, and service quality.',
      icon: Heart,
      color: 'rose'
    }
  ];

  const whoShouldAttend = [
    {
      title: 'Working Professionals',
      description: 'Enhance daily workplace communication, collaboration, and professional presence',
      icon: Briefcase,
      color: 'blue'
    },
    {
      title: 'Students & Fresh Graduates',
      description: 'Build foundational soft skills for successful career launch and employability',
      icon: GraduationCap,
      color: 'emerald'
    },
    {
      title: 'Team Leaders & Managers',
      description: 'Develop leadership, team motivation, and effective management capabilities',
      icon: Users,
      color: 'purple'
    },
    {
      title: 'Customer-Facing Roles',
      description: 'Master client communication, service excellence, and relationship building',
      icon: Headphones,
      color: 'amber'
    }
  ];

  const whyChennai = [
    {
      title: 'Major IT & Industrial Hub',
      description: 'Chennai hosts diverse workforce across IT, manufacturing, healthcare, education, and service industries',
      icon: Building2
    },
    {
      title: 'Global Workplace Demands',
      description: 'As organizations grow globally, communication, collaboration, and leadership become essential',
      icon: Globe
    },
    {
      title: 'Competitive Job Market',
      description: 'Employers consider soft skills as deciding factor during recruitment, promotions, and leadership selection',
      icon: Award
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "Soft Skills Training in Chennai",
    "description": "Comprehensive soft skills training covering communication, leadership, emotional intelligence, teamwork, and professional etiquette for students, professionals, and corporate teams in Chennai.",
    "provider": {
      "@type": "EducationalOrganization",
      "name": "ISDE Academy",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Chennai",
        "addressRegion": "Tamil Nadu",
        "addressCountry": "India"
      },
      "url": "https://isde.academy"
    },
    "courseCode": "SOFT-SKILLS",
    "educationalLevel": "Professional Development",
    "teaches": [
      "Communication Skills",
      "Leadership Development",
      "Emotional Intelligence",
      "Teamwork and Collaboration",
      "Professional Etiquette"
    ],
    "availableLanguage": "English",
    "locationCreated": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Chennai",
        "addressRegion": "Tamil Nadu",
        "addressCountry": "India"
      }
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is soft skills training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Soft skills training focuses on developing interpersonal, communication, behavioral, and emotional intelligence skills that enable individuals to work effectively with others. Unlike hard skills, which are technical and job-specific, soft skills are transferable across roles, industries, and career stages."
        }
      },
      {
        "@type": "Question",
        "name": "Why is soft skills training in Chennai essential?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Chennai hosts a diverse workforce across IT, manufacturing, healthcare, education, and service industries. As organizations grow globally, the ability to communicate, collaborate, and lead becomes essential. Employers increasingly consider soft skills as a deciding factor during recruitment, promotions, and leadership selection."
        }
      },
      {
        "@type": "Question",
        "name": "Who should attend soft skills training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Soft skills training is beneficial for students, fresh graduates, working professionals, team leaders, managers, and customer-facing roles. It's suitable for anyone looking to enhance their communication, leadership, teamwork, and professional presence."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{pageData.metaTitle}</title>
        <meta name="description" content={pageData.metaDescription} />
        <meta name="keywords" content={pageData.metaKeywords} />
        <link rel="canonical" href={pageData.canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageData.metaTitle} />
        <meta property="og:description" content={pageData.metaDescription} />
        <meta property="og:url" content={pageData.canonicalUrl} />
        <meta property="og:site_name" content="ISDE Academy" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageData.metaTitle} />
        <meta name="twitter:description" content={pageData.metaDescription} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-pink-700 text-white py-24 mt-20 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 -left-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-20 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-4000"></div>
          </div>

          <div className="absolute top-10 right-10 opacity-20">
            <MessageSquare className="h-32 w-32" />
          </div>
          <div className="absolute bottom-10 left-10 opacity-20">
            <Users className="h-24 w-24" />
          </div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Award className="h-5 w-5 text-yellow-200" />
                <span className="text-sm font-semibold">Professional & Corporate Programs</span>
              </motion.div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                {pageData.h1}
              </h1>

              <p className="text-xl text-blue-100 mb-8 leading-relaxed max-w-3xl mx-auto">
                In today's competitive job market, technical knowledge alone is no longer enough. Employers increasingly seek professionals who can communicate effectively, collaborate with teams, lead confidently, and adapt to changing workplace dynamics.
              </p>

              <div className="flex flex-wrap gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all"
                >
                  Enroll Now
                </Button>
                <Button
                  onClick={() => navigate('/training-calendar')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold px-8 py-6 text-lg"
                >
                  View Schedule
                </Button>
              </div>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F9FAFB"/>
            </svg>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Soft skills training in Chennai has become a critical investment for students, working professionals, and organizations looking to enhance productivity, employability, and leadership capability. Chennai, being one of India's major industrial and IT hubs, has a growing demand for structured soft skills development programs tailored to corporate, academic, and individual needs.
            </p>
          </div>
        </section>

        {/* What is Soft Skills Training */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">What Is Soft Skills Training?</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-4xl">
              Soft skills training focuses on developing interpersonal, communication, behavioral, and emotional intelligence skills that enable individuals to work effectively with others. Unlike hard skills, which are technical and job-specific, soft skills are transferable across roles, industries, and career stages.
            </p>
            <p className="text-lg text-gray-700 mb-4 font-semibold">Soft skills training in Chennai typically covers:</p>
            <ul className="grid md:grid-cols-2 gap-3 max-w-3xl">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                <span className="text-gray-700">Communication skills</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                <span className="text-gray-700">Leadership and management skills</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                <span className="text-gray-700">Teamwork and collaboration</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                <span className="text-gray-700">Problem-solving and decision-making</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                <span className="text-gray-700">Emotional intelligence and adaptability</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                <span className="text-gray-700">Professional etiquette and workplace behavior</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Why Soft Skills Training in Chennai */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Why Soft Skills Training in Chennai Is Essential?</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-12 max-w-4xl">
              Chennai hosts a diverse workforce across IT, manufacturing, healthcare, education, logistics, and service industries. As organizations grow globally, the ability to communicate, collaborate, and lead becomes essential.
            </p>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {whyChennai.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="h-full hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="bg-blue-50 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                          <IconComponent className="h-7 w-7 text-blue-600" />
                        </div>
                        <h3 className="text-xl font-bold mb-3 text-gray-900">{item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            <p className="text-lg text-gray-700 mb-4 font-semibold">Soft skills training in Chennai helps individuals and organizations to:</p>
            <ul className="grid md:grid-cols-2 gap-3 max-w-3xl">
              <li className="flex items-start gap-2">
                <ChevronRight className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Improve workplace communication and confidence</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Enhance leadership and managerial effectiveness</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Reduce workplace conflicts and misunderstandings</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Increase employability and career growth</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Build strong customer and client relationships</span>
              </li>
            </ul>
            <p className="text-gray-700 mt-6 italic">
              Employers increasingly consider soft skills as a deciding factor during recruitment, promotions, and leadership selection.
            </p>
          </div>
        </section>

        {/* Key Soft Skills Covered */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h3 className="text-3xl md:text-4xl font-bold mb-12 text-center text-gray-900">Key Soft Skills Covered in our Training Programs</h3>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {softSkillCategories.map((category, index) => {
                const IconComponent = category.icon;
                return (
                  <motion.article
                    key={category.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="h-full"
                  >
                    <Card className="h-full overflow-hidden hover:shadow-xl transition-all hover:-translate-y-2 group flex flex-col">
                      <div className={`bg-gradient-to-br ${category.gradient} p-6 text-white flex flex-col h-80`}>
                        <div className={`${category.bgColor} w-16 h-16 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform flex-shrink-0`}>
                          <IconComponent className={`h-8 w-8 ${category.color}`} />
                        </div>
                        <h4 className="text-2xl font-bold mb-3 flex-shrink-0">
                          {category.title}
                        </h4>
                        <p className="text-white/90 text-sm leading-relaxed flex-1 overflow-hidden">
                          {category.description}
                        </p>
                      </div>
                      <CardContent className="p-6 bg-white flex-1 flex flex-col">
                        <h5 className="font-bold text-sm text-gray-900 mb-3 uppercase tracking-wide">Key Topics:</h5>
                        <ul className="space-y-2 mb-4 flex-1">
                          {category.topics.map((topic, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <CheckCircle2 className={`h-4 w-4 ${category.color} flex-shrink-0 mt-0.5`} />
                              <span className="text-gray-600 text-sm">{topic}</span>
                            </li>
                          ))}
                        </ul>
                        <div className={`${category.bgColor} p-3 rounded-lg mt-auto`}>
                          <p className={`text-sm font-medium ${category.color}`}>{category.benefit}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h4 className="text-3xl md:text-4xl font-bold mb-12 text-center text-gray-900">Benefits of Soft Skills Training in Chennai</h4>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                const colors = {
                  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
                  blue: { bg: 'bg-blue-50', text: 'text-blue-600' },
                  purple: { bg: 'bg-purple-50', text: 'text-purple-600' },
                  orange: { bg: 'bg-orange-50', text: 'text-orange-600' },
                  rose: { bg: 'bg-rose-50', text: 'text-rose-600' }
                };
                const color = colors[benefit.color];

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="h-full hover:shadow-lg transition-all hover:-translate-y-1">
                      <CardContent className="p-6">
                        <div className={`${color.bg} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
                          <IconComponent className={`h-6 w-6 ${color.text}`} />
                        </div>
                        <h5 className="text-xl font-bold mb-2 text-gray-900">{benefit.title}</h5>
                        <p className="text-gray-600">{benefit.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Who Should Attend */}
        <section className="bg-gradient-to-br from-blue-50 to-purple-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h4 className="text-3xl md:text-4xl font-bold mb-12 text-center text-gray-900">Who Should Attend?</h4>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whoShouldAttend.map((audience, index) => {
                const IconComponent = audience.icon;
                const colors = {
                  blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
                  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' },
                  purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' },
                  amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' }
                };
                const color = colors[audience.color];

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className={`h-full bg-white border-2 ${color.border} hover:shadow-xl transition-all hover:-translate-y-2`}>
                      <CardContent className="p-6 text-center">
                        <div className={`${color.bg} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}>
                          <IconComponent className={`h-8 w-8 ${color.text}`} />
                        </div>
                        <h5 className="text-xl font-bold mb-3 text-gray-900">{audience.title}</h5>
                        <p className="text-gray-600 text-sm">{audience.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Corporate Training Section */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h4 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Corporate Soft Skills Training in Chennai</h4>
            <p className="text-lg text-gray-700 leading-relaxed mb-6 max-w-4xl">
              Many organizations invest in corporate soft skills training in Chennai to upskill their workforce. Corporate programs are customized based on:
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4 text-center">
                  <Building2 className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <p className="font-semibold text-gray-900">Industry requirements</p>
                </CardContent>
              </Card>
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-4 text-center">
                  <Users className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                  <p className="font-semibold text-gray-900">Organizational culture</p>
                </CardContent>
              </Card>
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-4 text-center">
                  <Target className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
                  <p className="font-semibold text-gray-900">Employee roles and skill gaps</p>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4 text-center">
                  <Rocket className="h-8 w-8 text-amber-600 mx-auto mb-2" />
                  <p className="font-semibold text-gray-900">Business objectives</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4">
            <h4 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Why Choose ISDE Academy for Professional Soft Skills Training in Chennai?</h4>
            <p className="text-lg text-gray-700 mb-8 max-w-4xl">
              Choosing the right training provider is crucial. ISDE Academy's soft skills training institutes in Chennai offer:
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Award, title: 'Experienced corporate trainers', color: 'blue' },
                { icon: BookOpen, title: 'Industry-relevant content', color: 'emerald' },
                { icon: Users, title: 'Practical exercises and simulations', color: 'purple' },
                { icon: Target, title: 'Customized training modules', color: 'orange' },
                { icon: TrendingUp, title: 'Measurable learning outcomes', color: 'rose' }
              ].map((item, index) => {
                const IconComponent = item.icon;
                const colors = {
                  blue: { bg: 'bg-blue-50', text: 'text-blue-600' },
                  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
                  purple: { bg: 'bg-purple-50', text: 'text-purple-600' },
                  orange: { bg: 'bg-orange-50', text: 'text-orange-600' },
                  rose: { bg: 'bg-rose-50', text: 'text-rose-600' }
                };
                const color = colors[item.color];

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="hover:shadow-lg transition-shadow">
                      <CardContent className="p-6 flex items-center gap-4">
                        <div className={`${color.bg} p-3 rounded-lg flex-shrink-0`}>
                          <IconComponent className={`h-6 w-6 ${color.text}`} />
                        </div>
                        <p className="font-semibold text-gray-900">{item.title}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            <p className="text-gray-700 mt-8 text-lg italic max-w-3xl">
              Quality training ensures participants can apply learned skills directly in the workplace.
            </p>
          </div>
        </section>

        {/* Future Scope */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h4 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Future Scope of Soft Skills Training in Chennai</h4>
            <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-4xl">
              With automation, AI, and digital transformation reshaping industries, human skills such as communication, adaptability, creativity, and leadership will become even more valuable. Soft skills training in Chennai will continue to play a vital role in:
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: Target, text: 'Leadership development' },
                { icon: Users, text: 'Workforce transformation' },
                { icon: TrendingUp, text: 'Employability enhancement' },
                { icon: Shield, text: 'Organizational sustainability' }
              ].map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200 hover:shadow-lg transition-all hover:-translate-y-1">
                      <CardContent className="p-6 flex items-center gap-3">
                        <IconComponent className="h-8 w-8 text-blue-600 flex-shrink-0" />
                        <p className="font-semibold text-gray-900">{item.text}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Ready to Transform Your Professional Skills?
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Join thousands of professionals in Chennai who have enhanced their careers with our comprehensive soft skills training programs
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all"
                >
                  Enroll Now
                </Button>
                <Button
                  onClick={() => navigate('/training-calendar')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold px-8 py-6 text-lg"
                >
                  View Training Calendar
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SoftSkillsTraining;
